/**
 * The PIP's candles as geometry, so the canvas only has to stroke them.
 *
 * The same split `@qufolio/chart-engine`'s `plan/` layer makes one level up:
 * everything decidable — the price range, where each candle sits, how wide a
 * body is — is computed here and tested in Node, and the imperative half is a
 * loop over the result with no arithmetic in it. A mini chart is small enough
 * that the temptation is to do both in the draw call, and that is exactly how it
 * ends up untestable.
 *
 * ## Why not SciChart
 *
 * A second `SciChartSurface` brings every trap `docs/features/charts.md`
 * records: the div needs a unique `id` or the new surface deletes the first
 * one's render destination, the `modifierGroup` must not be shared or a disposed
 * surface freezes the other chart's crosshair for good, and `delete()` mutates
 * the div it was handed. That is a large amount of care for a 180-pixel
 * thumbnail with no axes, no crosshair and no interaction.
 *
 * ## What it deliberately does not draw
 *
 * No axis, no gridlines, no last-value marker, no volume. The PIP answers one
 * question — what does the higher timeframe look like — and every one of those
 * costs pixels the panel does not have. Read the numbers on the chart it sits
 * in.
 */

/** One bar, as the transport decodes it. */
export interface Bar {
  open: number;
  high: number;
  low: number;
  close: number;
}

/** One candle, in the canvas' own pixel space, ready to fill and stroke. */
export interface Candle {
  /** Centre of the bar, where the wick is drawn. */
  x: number;
  /** Left edge of the body. */
  left: number;
  /** Body width; never below one pixel, so a doji is still visible. */
  width: number;
  /** Top of the body — the higher of open and close, in screen coordinates. */
  top: number;
  /** Body height; never below one pixel, for the same reason as the width. */
  height: number;
  /** Wick extent, in screen coordinates. */
  wickTop: number;
  wickBottom: number;
  /** Close at or above open — which colour the caller reaches for. */
  up: boolean;
}

export interface CandleLayout {
  candles: Candle[];
  /** The padded price range the layout was built against. */
  low: number;
  high: number;
}

/**
 * Room left above and below the extremes, as a fraction of the range.
 *
 * A candle touching the top edge of a 100px panel reads as clipped rather than
 * as the high, and the panel has no axis to say otherwise.
 */
const RANGE_PADDING = 0.08;

/** How much of a slot the body fills; the rest is the gap between candles. */
const BODY_FRACTION = 0.62;

/**
 * Lay out `bars` inside a `width` × `height` box with `pad` pixels of margin.
 *
 * Returns an empty layout for no bars or no usable prices rather than throwing:
 * a PIP whose data has not arrived draws nothing, which is the correct picture,
 * and a panel that threw would take the chart's render down with it.
 */
export function candleLayout(
  bars: readonly Bar[],
  width: number,
  height: number,
  pad = 4,
): CandleLayout {
  const empty: CandleLayout = { candles: [], low: 0, high: 0 };
  if (bars.length === 0 || width <= pad * 2 || height <= pad * 2) return empty;

  // **All four prices, not just the wick.** Ranging on high and low alone is the
  // obvious reading and drops any bar whose wick is missing — the range comes
  // out infinite and the whole panel draws nothing, which looks exactly like
  // data that never arrived. Folding open and close in costs two comparisons and
  // means a body always has a range to be scaled against.
  let lo = Infinity;
  let hi = -Infinity;
  for (const b of bars) {
    for (const v of [b.open, b.high, b.low, b.close]) {
      if (!Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return empty;

  // A flat series has no range to scale against. Widening it by a hair puts the
  // line through the middle of the panel instead of dividing by zero — the same
  // answer an auto-ranging axis gives a constant series.
  if (hi === lo) {
    const nudge = Math.abs(hi) * 0.005 || 0.5;
    lo -= nudge;
    hi += nudge;
  } else {
    const margin = (hi - lo) * RANGE_PADDING;
    lo -= margin;
    hi += margin;
  }

  const plotW = width - pad * 2;
  const plotH = height - pad * 2;
  const span = hi - lo;
  const y = (v: number) => pad + plotH - ((v - lo) / span) * plotH;

  const slot = plotW / bars.length;
  const body = Math.max(1, slot * BODY_FRACTION);

  const candles: Candle[] = [];
  bars.forEach((b, i) => {
    if (!Number.isFinite(b.open) || !Number.isFinite(b.close)) return;
    const cx = pad + i * slot + slot / 2;
    const yo = y(b.open);
    const yc = y(b.close);
    const top = Math.min(yo, yc);
    candles.push({
      x: cx,
      left: cx - body / 2,
      width: body,
      top,
      // One pixel minimum: a bar whose open equals its close is a real bar and a
      // zero-height rectangle paints nothing at all.
      height: Math.max(1, Math.abs(yc - yo)),
      wickTop: Number.isFinite(b.high) ? y(b.high) : top,
      wickBottom: Number.isFinite(b.low) ? y(b.low) : top,
      up: b.close >= b.open,
    });
  });

  return { candles, low: lo, high: hi };
}
